<template>
  <div>
    <!-- Desktop: tabel biasa -->
    <div class="table-responsive desktop-table">
      <table class="table table-bordered table-striped">
        <thead>
          <tr>
            <th scope="col" class="text-center">Assets</th>
            <th scope="col" class="text-center">Bunker Start</th>
            <th scope="col" class="text-center">Bunker End</th>
            <th scope="col" class="text-center">Location</th>
            <th scope="col" class="text-center">Fuel Type</th>
            <th scope="col" class="text-center">Bunker Type</th>
            <th scope="col" class="text-center">Target Bunker ({{ bunkerUnit }})</th>
            <th scope="col" class="text-center">-</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td :colspan="tableColumnCount" class="text-center text-danger">
              <wave-loader />
            </td>
          </tr>
          <tr v-for="item in bunkeringLogData" :key="item.bklgId">
            <td class="text-center cell-content">{{ item.massName || '-' }}</td>
            <td class="text-center cell-content">{{ item.bklgStartDatetime || '-' }}</td>
            <td class="text-center cell-content">{{ item.bklgEndDatetime || '-' }}</td>
            <td class="text-center cell-content">{{ item.mbloName || '-' }}</td>
            <td class="text-center cell-content">{{ item.mftyName || '-' }}</td>
            <td class="text-center cell-content">{{ item.bklgType || '-' }}</td>
            <td class="text-center cell-content">{{ item.bklgTargetBunkerFormatted || '-' }}</td>
            <td class="text-center cell-content">
              <button-options :access="'master_bunker_log'" :hideAdd="true" :hideDelete="false" :hideDetail="false"
                :hideEdit="false" @onDeleteClicked="() => $emit('delete', item.rawData)"
                @onEditClicked="() => $emit('edit', item.rawData)" @onViewClicked="() => $emit('view', item.rawData)" />
            </td>
          </tr>
          <tr v-if="!bunkeringLogData.length && !isLoading">
            <td :colspan="tableColumnCount" class="text-center">No data available</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile: card layout -->
    <div class="mobile-cards">
      <div v-if="isLoading" class="text-center py-4">
        <wave-loader />
      </div>
      <div v-else-if="!bunkeringLogData.length" class="text-center py-4 text-muted">
        No data available
      </div>
      <div v-else class="bunkering-card-list">
        <div v-for="item in bunkeringLogData" :key="item.bklgId" class="bunkering-card">
          <div class="bunkering-card-header">
            <span class="bunkering-card-title">{{ item.massName || '-' }}</span>
          </div>
          <div class="bunkering-card-body">
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Bunker Start</span>
              <span class="bunkering-card-value">{{ item.bklgStartDatetime || '-' }}</span>
            </div>
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Bunker End</span>
              <span class="bunkering-card-value">{{ item.bklgEndDatetime || '-' }}</span>
            </div>
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Location</span>
              <span class="bunkering-card-value">{{ item.mbloName || '-' }}</span>
            </div>
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Fuel Type</span>
              <span class="bunkering-card-value">{{ item.mftyName || '-' }}</span>
            </div>
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Bunker Type</span>
              <span class="bunkering-card-value">{{ item.bklgType || '-' }}</span>
            </div>
            <div class="bunkering-card-row">
              <span class="bunkering-card-label">Target Bunker ({{ bunkerUnit }})</span>
              <span class="bunkering-card-value">{{ item.bklgTargetBunkerFormatted || '-' }}</span>
            </div>
          </div>
          <div class="bunkering-card-footer">
            <button-options :access="'master_bunker_log'" :hideAdd="true" :hideDelete="false" :hideDetail="false"
              :hideEdit="false" @onDeleteClicked="() => $emit('delete', item.rawData)"
              @onEditClicked="() => $emit('edit', item.rawData)" @onViewClicked="() => $emit('view', item.rawData)" />
          </div>
        </div>
      </div>
    </div>

    <b-pagination v-model="pagination.current" :per-page="pagination.pageSize" :total-rows="pagination.totalSize"
      @change="onPageChanged"></b-pagination>
  </div>
</template>

<script>
import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
import ButtonOptions from '@/components/directives/button-options';
import { formatNumOrRaw } from '@/util/number';

export default {
  name: 'BunkeringLogTable',
  components: {
    ButtonOptions,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({
        current: 1,
        pageSize: 10,
        totalSize: 0,
      }),
    },
    bunkerUnit: {
      type: String,
      default: 'kl',
    },
    showSoundingData: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tableColumnCount() {
      return this.showSoundingData ? 12 : 10;
    },
    bunkeringLogData() {
      const formatedData = this.data.map((item) => {
        const toUnit = (value) => (this.bunkerUnit === 'kl' ? value * 0.001 : value);

        const targetBunker = parseFloat(item.bklgTargetBunker || 0);
        const targetBunkerInUnit = toUnit(targetBunker);

        return {
          bklgId: item?.bklgId,
          bklgStartDatetime: fromApiDatetime(item?.bklgStartDatetime).format(DISPLAY_DATETIME),
          bklgEndDatetime: fromApiDatetime(item?.bklgEndDatetime).format(DISPLAY_DATETIME),
          massName: item?.bklgMass?.massName,
          mbloName: item?.bklgMblo?.mbloName,
          mftyName: item?.bklgMfty?.mftyName,
          bklgType: item?.bklgType,
          bklgTargetBunkerFormatted: formatNumOrRaw(targetBunkerInUnit),
          rawData: item,
        };
      });

      // Urutan udah diurus Index.getData() dari data mentah; nyortir ulang di sini
      // berarti mem-parse string tampilan.
      return formatedData;
    },
  },
  methods: {
    onPageChanged(page) {
      this.$emit('page-changed', page);
    },
  },
};
</script>

<style scoped>
/* Show/hide per breakpoint */
.desktop-table { display: block; }
.mobile-cards  { display: none; }

@media (max-width: 767px) {
  .desktop-table { display: none; }
  .mobile-cards  { display: block; }
}

/* Desktop table */
.cell-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table th,
.table td {
  min-width: 200px;
  max-width: 300px;
}

/* Mobile card layout */
.bunkering-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0 4px;
}

.bunkering-card {
  border: 1px solid var(--mz-border);
  border-radius: var(--mz-radius-sm);
  overflow: hidden;
  background: var(--mz-surface);
  box-shadow: var(--mz-shadow-soft);
}

.bunkering-card-header {
  background: var(--mz-table-head-bg);
  color: var(--mz-on-primary);
  padding: 10px 14px;
}

.bunkering-card-title {
  font-weight: 600;
  font-size: var(--mz-fs-base);
}

.bunkering-card-body {
  padding: 10px 14px;
}

.bunkering-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}

.bunkering-card-row:last-child {
  border-bottom: none;
}

.bunkering-card-label {
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-subtle);
  font-weight: 500;
  flex-shrink: 0;
  min-width: 120px;
}

.bunkering-card-value {
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-emphasis);
  text-align: right;
}

.bunkering-card-footer {
  padding: 8px 14px;
  background: var(--mz-surface-2);
  border-top: 1px solid var(--mz-border);
  display: flex;
  justify-content: flex-end;
}
</style>
